import { useSyncExternalStore } from 'react';
import utils from './utils';
import { API } from '../service/ProductsService';

// מוצרים מועדפים — store משותף (כמו basketStore), אבל נשמר בשרת (userFavorites, Profile.sql): עובר בין מכשירים.
// כאן רק ה-ids, ל-❤ בכל שורת מוצר (ProductRow). הרשימה המלאה עם המחירים — בעמוד הפרופיל (spGetFavorites).
// נטען פעם אחת כשהאתר נפתח עם משתמש מחובר, ושוב אחרי כניסה / יציאה (ZolBakfarApp → favorites.reload()).
// id = platformProductId (item.id בחיפוש).

let ids = new Set();          // אובייקט חדש בכל שינוי — useSyncExternalStore משווה לפי הפניה
let loadId = 0;               // תשובה ישנה (לפני יציאה / כניסה של משתמש אחר) לא דורסת
let loaded = false;           // ה-ids הגיעו מהשרת (עד אז הרשימה הריקה לא אומרת שאין מועדפים)
const listeners = new Set();
const notify = () => listeners.forEach((l) => l());

const setIds = (next) => { ids = next; notify(); };

const load = async () => {
  const id = ++loadId;
  loaded = false;
  if (!utils.getCurrentUser()) { setIds(new Set()); return; }
  try {
    const resp = await API.getFavoriteIds();
    if (id !== loadId) return;
    if (resp?.[0]?.[0]?.code === 0) {
      loaded = true;
      setIds(new Set((resp[1] || []).map((r) => r.itemId)));
    }
  } catch (e) { /* בלי ❤ מסומנים — האתר עדיין עובד */ }
};

export const favorites = {
  subscribe: (listener) => { listeners.add(listener); return () => listeners.delete(listener); },
  getIds: () => ids,
  has: (itemId) => ids.has(itemId),
  isLoaded: () => loaded,
  reload: load,

  // עדכון אופטימי: ה-❤ משתנה מיד, וחוזר אחורה אם השרת נכשל. מחזיר { ok, msg }
  set: async (itemId, isFavorite) => {
    const before = ids;
    const next = new Set(ids);
    if (isFavorite) next.add(itemId); else next.delete(itemId);
    setIds(next);
    try {
      const resp = await API.setFavorite(itemId, isFavorite);
      const status = resp?.[0]?.[0];
      if (status?.code === 0) return { ok: true };
      if (ids === next) setIds(before);
      return { ok: false, msg: status?.msg || 'שגיאה בשמירת המועדפים' };
    } catch (err) {
      if (ids === next) setIds(before);
      return { ok: false, msg: err.message || 'שגיאה בשמירת המועדפים' };
    }
  },
  toggle: (itemId) => favorites.set(itemId, !ids.has(itemId)),
};

load();

// תשובת spGetFavorites (API.getFavorites) → מה ש-ProductRow צריך. משותף לפרופיל, לחלון המועדפים ולעמוד הראשי.
// dropsByItem: { [itemId]: { [storeId]: { oldPrice, newPrice, changedAt } } } — "ירד מחיר!" ב-7 הימים האחרונים
export const parseFavorites = (resp) => {
  const pricesByItem = {};
  (resp[3] || []).forEach((p) => {
    if (!pricesByItem[p.itemId]) pricesByItem[p.itemId] = {};
    pricesByItem[p.itemId][p.storeId] = p;
  });
  const dropsByItem = {};
  (resp[5] || []).forEach((d) => {
    if (!dropsByItem[d.itemId]) dropsByItem[d.itemId] = {};
    dropsByItem[d.itemId][d.storeId] = d;
  });
  return { stores: resp[1] || [], items: resp[2] || [], pricesByItem, unavailable: resp[4] || [], dropsByItem };
};

export const DROP_BADGE = { text: '⬇ ירד מחיר!', tone: 'drop' };

// ה-ids העדכניים; הקומפוננטה מתרנדרת בכל שינוי
export const useFavoriteIds = () => useSyncExternalStore(favorites.subscribe, favorites.getIds);
